import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useBetaData } from '../../../contexts/BetaDataContext';
import { useToast } from '../../common/Toast';
import { updateUserSettings } from '../../../api/profiles';
import './BetaDailyReportProjectSelector.scss';

const BetaDailyReportProjectSelector = ({ isOpen, onClose, onSuccess }) => {
  const { profile, patchProfileSettings } = useAuth();
  const { projects } = useBetaData();
  const toast = useToast();
  const [orderedProjectIds, setOrderedProjectIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // 初始化已選擇的專案（保持順序）
  useEffect(() => {
    if (isOpen && profile?.settings?.daily_report_visible_project_ids) {
      setOrderedProjectIds(profile.settings.daily_report_visible_project_ids);
    } else if (isOpen && projects.length > 0) {
      setOrderedProjectIds(projects.map(p => p.id));
    }
  }, [isOpen, profile, projects]);

  const toggleProject = (projectId) => {
    if (orderedProjectIds.includes(projectId)) {
      setOrderedProjectIds(orderedProjectIds.filter(id => id !== projectId));
    } else {
      setOrderedProjectIds([...orderedProjectIds, projectId]);
    }
  };

  const moveUp = (projectId) => {
    const index = orderedProjectIds.indexOf(projectId);
    if (index > 0) {
      const newOrder = [...orderedProjectIds];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      setOrderedProjectIds(newOrder);
    }
  };

  const moveDown = (projectId) => {
    const index = orderedProjectIds.indexOf(projectId);
    if (index < orderedProjectIds.length - 1 && index !== -1) {
      const newOrder = [...orderedProjectIds];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      setOrderedProjectIds(newOrder);
    }
  };

  const toggleAll = (selectAll) => {
    if (selectAll) {
      setOrderedProjectIds(projects.map(p => p.id));
    } else {
      setOrderedProjectIds([]);
    }
  };

  const handleSave = async () => {
    try {
      const newSettings = {
        ...(profile?.settings || {}),
        daily_report_visible_project_ids: orderedProjectIds
      };

      const { error } = await updateUserSettings(profile.id, newSettings);
      if (error) throw error;

      // 局部更新本地 profile.settings，避免整個 profile 重新載入
      patchProfileSettings({ daily_report_visible_project_ids: orderedProjectIds });
      toast.success('Daily Report 展示專案已更新');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('更新設定失敗:', error);
      toast.error('更新失敗：' + error.message);
    }
  };

  const filteredProjects = useMemo(() => {
    let list = projects;

    if (searchTerm) {
      list = list.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    const selectedProjects = orderedProjectIds
      .map(id => list.find(p => p.id === id))
      .filter(p => p);

    const unselectedProjects = list.filter(
      p => !orderedProjectIds.includes(p.id)
    );

    return [...selectedProjects, ...unselectedProjects];
  }, [projects, searchTerm, orderedProjectIds]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="daily-report-project-selector" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>設定我在 Daily Report 中展示的專案</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-description">
          <p>選擇您希望在 Daily Report 中展示給團隊成員看的專案。</p>
          <p>只有勾選的專案會在別人查看您的 Daily Report 時顯示。</p>
        </div>

        <div className="modal-toolbar">
          <input
            type="text"
            className="search-input"
            placeholder="搜尋專案..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="toolbar-actions">
            <button className="toggle-all-btn" onClick={() => toggleAll(true)}>
              全選
            </button>
            <button className="toggle-all-btn" onClick={() => toggleAll(false)}>
              全不選
            </button>
            <span className="selected-count">
              已選擇: {orderedProjectIds.length} / {projects.length} 個
            </span>
          </div>
        </div>

        <div className="modal-body">
          {filteredProjects.length === 0 ? (
            <div className="empty-state">沒有符合的專案</div>
          ) : (
            <div className="projects-list">
              {filteredProjects.map((project) => {
                const isSelected = orderedProjectIds.includes(project.id);
                const selectedIndex = orderedProjectIds.indexOf(project.id);
                const isFirst = selectedIndex === 0;
                const isLast = selectedIndex === orderedProjectIds.length - 1;

                return (
                  <div key={project.id} className={`project-item ${isSelected ? 'selected' : ''}`}>
                    <label className="project-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleProject(project.id)}
                      />
                      <span className="project-info">
                        <span
                          className="project-icon"
                          style={{ color: project.color }}
                        >
                          {project.icon || '📊'}
                        </span>
                        <span className="project-name">{project.name}</span>
                      </span>
                    </label>

                    {isSelected && (
                      <div className="sort-buttons">
                        <button
                          className="sort-btn"
                          onClick={() => moveUp(project.id)}
                          disabled={isFirst}
                          title="上移"
                        >
                          ▲
                        </button>
                        <button
                          className="sort-btn"
                          onClick={() => moveDown(project.id)}
                          disabled={isLast}
                          title="下移"
                        >
                          ▼
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>取消</button>
          <button className="save-btn" onClick={handleSave}>儲存</button>
        </div>
      </div>
    </div>
  );
};

export default BetaDailyReportProjectSelector;
